/**
 * Dashboard Layout
 *
 * Main layout for authenticated pages with sidebar navigation
 */

import { useState, useEffect } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { InterruptorDeTema } from '@/presentation/components/ui/InterruptorDeTema'
import { nombreParaMostrar } from '@/domain/user/types'
import {
  LayoutDashboard,
  Users,
  FileImage,
  ClipboardList,
  FileText,
  Shield,
  FolderSync,
  BookOpen,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Mic,
  Building2,
  Crown,
  Layers,
  Flag,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CreditCard,
  Activity,
} from 'lucide-react'

import { useAuth } from '@/application/auth'
import { cn } from '@/lib/utils'
import { APP_ROUTES, USER_ROLES } from '@/config/constants'
import { Button } from '@/presentation/components/ui/Button'
import { NotificationDropdown } from '@/presentation/components/notifications/NotificationDropdown'
import { SidebarLogo } from '@/presentation/components/ui/AppLogo'
import { LimiteDeError } from '@/presentation/components/ui/LimiteDeError'
import { PRODUCTO } from '@/config/producto'

// =============================================================================
// Navigation Items
// =============================================================================

interface NavItem {
  label: string
  icon: React.ReactNode
  href: string
  roles?: string[]
  exact?: boolean
  /**
   * Modulo del que depende esta opcion.
   *
   * Una IPS que no contrato la agenda no debe ver "Citas": ofrecer lo que no
   * se compro genera una llamada de soporte por cada menu. El servidor niega
   * las rutas del modulo por su cuenta; esto solo evita ofrecerlas.
   */
  modulo?: string
}

// Items operativos clinicos. SUPER_ADMIN intencionalmente excluido —
// su rol es operar el producto (control plane), no la operacion clinica
// de las IPS. Eso lo hace el ADMIN de cada institucion.
const mainNavItems: NavItem[] = [
  {
    label: 'Dashboard',
    icon: <LayoutDashboard className="h-5 w-5" />,
    href: APP_ROUTES.DASHBOARD,
    exact: true,
    roles: [
      USER_ROLES.ADMIN,
      USER_ROLES.RADIOLOGIST,
      USER_ROLES.TECHNICIAN,
      USER_ROLES.DOCTOR,
    ],
  },
  {
    label: 'Mi Trabajo',
    icon: <ClipboardList className="h-5 w-5" />,
    href: APP_ROUTES.WORKLIST,
    roles: [USER_ROLES.ADMIN, USER_ROLES.RADIOLOGIST, USER_ROLES.TECHNICIAN],
  },
  {
    label: 'Informes',
    icon: <FileText className="h-5 w-5" />,
    href: APP_ROUTES.REPORTS,
    roles: [USER_ROLES.ADMIN, USER_ROLES.RADIOLOGIST, USER_ROLES.DOCTOR],
  },
  {
    label: 'Estudios',
    icon: <FileImage className="h-5 w-5" />,
    href: APP_ROUTES.STUDIES,
    // El radiologo lee estudios: es su trabajo, y no tenerlo en el menu lo
    // obligaba a llegar dando rodeos desde la worklist. El medico remitente
    // tambien los necesita: ver solo el informe y no las imagenes deja
    // incompleta la consulta.
    roles: [
      USER_ROLES.ADMIN,
      USER_ROLES.TECHNICIAN,
      USER_ROLES.RADIOLOGIST,
      USER_ROLES.DOCTOR,
    ],
  },
  {
    label: 'Pacientes',
    icon: <Users className="h-5 w-5" />,
    href: APP_ROUTES.PATIENTS,
    // El radiologo tiene patient:view:all y la ruta ya lo admitia: solo
    // faltaba la puerta de entrada.
    roles: [
      USER_ROLES.ADMIN,
      USER_ROLES.TECHNICIAN,
      USER_ROLES.DOCTOR,
      USER_ROLES.RADIOLOGIST,
    ],
  },
  {
    modulo: 'citas',
    label: 'Citas',
    icon: <Calendar className="h-5 w-5" />,
    href: APP_ROUTES.APPOINTMENTS,
    /**
     * Solo quien agenda de verdad.
     *
     * Estaban tambien el radiologo y el medico remitente, y ninguno de los
     * dos puede usar la pantalla: la agenda exige `appointment:view:all` y
     * ellos solo tienen `appointment:view:own`. El menu les ofrecia una
     * opcion que el servidor les niega, que es peor que no ofrecerla.
     *
     * Ademas no es su trabajo. Un radiologo no da citas: lo suyo es la lista
     * de trabajo, y para eso tiene "Mi Trabajo". Lo señalo Edelmiro el
     * 22/09/2026 revisando el producto.
     *
     * `appointment:view:own` ya tiene su pantalla: "Mi agenda", justo debajo.
     * Ahi el radiologo ve lo suyo sin entrar a la pantalla de quien agenda.
     */
    roles: [USER_ROLES.ADMIN, USER_ROLES.TECHNICIAN],
  },
  {
    modulo: 'citas',
    label: 'Mi agenda',
    icon: <CalendarCheck className="h-5 w-5" />,
    href: APP_ROUTES.MI_AGENDA,
    /**
     * La otra cara de "Citas".
     *
     * Esa pantalla es de quien agenda y exige `appointment:view:all`. Esta es
     * de quien ATIENDE y exige `view:own`: muestra unicamente las citas en las
     * que el usuario es el profesional.
     *
     * El radiologo es hoy el unico que figura como profesional de una cita
     * (la pantalla de agendar elige entre radiologos), asi que es el unico a
     * quien le dice algo. Se ofrece tambien al administrador porque necesita
     * poder ver lo que ve su gente cuando alguien le pregunta.
     */
    roles: [USER_ROLES.RADIOLOGIST, USER_ROLES.ADMIN],
  },
  {
    modulo: 'transcripcion',
    label: 'Transcripción',
    icon: <Mic className="h-5 w-5" />,
    href: APP_ROUTES.TRANSCRIPTION.QUEUE,
    roles: [USER_ROLES.TRANSCRIPTOR],
  },
]

const adminNavItems: NavItem[] = [
  {
    label: 'Usuarios',
    icon: <Users className="h-5 w-5" />,
    href: APP_ROUTES.ADMIN.USERS,
  },
  {
    label: 'Tipos de Estudio',
    icon: <Layers className="h-5 w-5" />,
    href: APP_ROUTES.ADMIN.STUDY_TYPES,
  },
  {
    label: 'Prioridades',
    icon: <Flag className="h-5 w-5" />,
    href: APP_ROUTES.ADMIN.PRIORITIES,
  },
  {
    label: 'Agendas',
    icon: <CalendarClock className="h-5 w-5" />,
    href: APP_ROUTES.ADMIN_SCHEDULES,
  },
  {
    label: 'Plantillas',
    icon: <FileText className="h-5 w-5" />,
    href: APP_ROUTES.ADMIN.TEMPLATES,
  },
  {
    /**
     * La pantalla existia y funcionaba, pero no habia forma de llegar a ella
     * salvo escribiendo la direccion: ninguna IPS iba a usar una funcion que
     * su administrador no ve. Va condicionada al modulo, como Citas y
     * Transcripcion.
     */
    modulo: 'carpeta_vigilada',
    label: 'Carpetas Vigiladas',
    icon: <FolderSync className="h-5 w-5" />,
    href: APP_ROUTES.ADMIN.HOT_FOLDERS,
  },
  {
    label: 'Auditoría',
    icon: <Shield className="h-5 w-5" />,
    href: APP_ROUTES.ADMIN.AUDIT,
  },
  {
    label: 'Sedes y equipos',
    icon: <Building2 className="h-5 w-5" />,
    href: APP_ROUTES.ADMIN.SEDES,
  },
  {
    label: 'Configuración',
    icon: <Settings className="h-5 w-5" />,
    href: APP_ROUTES.ADMIN.SETTINGS,
  },
]

// Super Admin only items — control plane del producto JH LUMINA.
// El SUPER_ADMIN no entra a la operacion clinica (eso es para los
// ADMIN de cada IPS), opera el negocio: clientes, licencias, salud.
const superAdminNavItems: NavItem[] = [
  {
    label: 'Inicio',
    icon: <Crown className="h-5 w-5" />,
    href: APP_ROUTES.SUPER_ADMIN.DASHBOARD,
    exact: true,
  },
  {
    label: 'Instituciones',
    icon: <Building2 className="h-5 w-5" />,
    href: APP_ROUTES.SUPER_ADMIN.INSTITUTIONS,
  },
  {
    label: 'Planes',
    icon: <CreditCard className="h-5 w-5" />,
    href: APP_ROUTES.SUPER_ADMIN.PLANS,
  },
  {
    label: 'Catalogo global',
    icon: <Layers className="h-5 w-5" />,
    href: APP_ROUTES.SUPER_ADMIN.CATALOG,
  },
  {
    label: 'Auditoria global',
    icon: <Shield className="h-5 w-5" />,
    href: APP_ROUTES.SUPER_ADMIN.AUDIT,
  },
  {
    label: 'Sistema',
    icon: <Activity className="h-5 w-5" />,
    href: APP_ROUTES.SUPER_ADMIN.SYSTEM,
  },
]

// =============================================================================
// Component
// =============================================================================

export function DashboardLayout() {
  const { user, logout, isLoading } = useAuth()
  const modulos = user?.modulos
  const location = useLocation()
  /**
   * El menu lateral arranca segun la pantalla, no siempre abierto.
   *
   * Estaba fijo en `true` sin mirar el dispositivo. En un telefono de 375px
   * el menu de 256px ocupaba el 68% del ancho y empujaba el contenido a una
   * columna de unos 120px: la aplicacion entera era inutilizable en movil, y
   * no era un problema de una pantalla sino del armazon de todas.
   *
   * Por debajo del punto de corte se comporta como cajon superpuesto: no
   * empuja el contenido, se pone encima y se cierra al elegir una opcion,
   * que es lo que espera quien usa un telefono o una tableta de pie.
   */
  const esPantallaAncha = () =>
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 768px)').matches

  const [sidebarOpen, setSidebarOpen] = useState(esPantallaAncha)
  const [enPantallaAncha, setEnPantallaAncha] = useState(esPantallaAncha)

  useEffect(() => {
    const consulta = window.matchMedia('(min-width: 768px)')
    const alCambiar = (e: MediaQueryListEvent) => {
      setEnPantallaAncha(e.matches)
      // Al pasar a estrecho se cierra; al volver a ancho se abre. Conservar
      // el estado anterior deja el cajon tapando la pantalla al girar el
      // dispositivo.
      setSidebarOpen(e.matches)
    }
    consulta.addEventListener('change', alCambiar)
    return () => consulta.removeEventListener('change', alCambiar)
  }, [])

  // Al navegar en pantalla estrecha, el cajon se cierra solo. Dejarlo abierto
  // tapando la pantalla a la que se acaba de entrar obliga a un toque extra
  // en cada navegacion, y son muchas al dia.
  useEffect(() => {
    if (!enPantallaAncha) setSidebarOpen(false)
  }, [location.pathname, enPantallaAncha])

  // Menus siempre abiertos por defecto para mejor UX
  const [adminMenuOpen, setAdminMenuOpen] = useState(true)
  const [superAdminMenuOpen, setSuperAdminMenuOpen] = useState(true)

  // Reset menu state when user changes (prevents state leakage between users)
  useEffect(() => {
    setSidebarOpen(true)
    setAdminMenuOpen(true)
    setSuperAdminMenuOpen(true)
  }, [user?.id])

  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN
  // SUPER_ADMIN NO ve la seccion "Administración" (clinica) porque ese
  // bloque pertenece al ADMIN de cada IPS. El SUPER_ADMIN tiene su
  // propio bloque (superAdminNavItems) enfocado en gestion de producto.
  const isAdmin = !isSuperAdmin && user?.role === USER_ROLES.ADMIN

  const filteredNavItems = mainNavItems.filter((item) => {
    // Lo que la institucion no tiene habilitado no se ofrece.
    if (item.modulo && modulos?.[item.modulo] === false) return false
    if (!item.roles) return true
    return user && item.roles.includes(user.role)
  })

  const NavLinkItem = ({ item }: { item: NavItem }) => (
    <NavLink
      to={item.href}
      end={item.exact}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
          'text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-hover)]',
          isActive && 'bg-[var(--sidebar-active)] font-medium'
        )
      }
    >
      {item.icon}
      {sidebarOpen && <span>{item.label}</span>}
    </NavLink>
  )

  return (
    <div className="min-h-screen alto-de-pantalla bg-[var(--background)] flex">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-[var(--sidebar)] transition-all duration-300',
          // En pantalla ancha ocupa sitio y se puede colapsar a iconos.
          // En estrecha es un cajon: ancho completo del menu, o fuera.
          enPantallaAncha
            ? sidebarOpen
              ? 'w-64'
              : 'w-16'
            : sidebarOpen
              ? 'w-64 shadow-2xl'
              : 'w-64 -translate-x-full'
        )}
        aria-hidden={!enPantallaAncha && !sidebarOpen}
      >
        {/* Logo */}
        <div className="p-4 border-b border-white/10">
          <SidebarLogo collapsed={!sidebarOpen} />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {filteredNavItems.map((item) => (
            <NavLinkItem key={item.href} item={item} />
          ))}

          {/* Admin Section */}
          {isAdmin && (
            <div className="pt-4 mt-4 border-t border-white/10">
              {sidebarOpen ? (
                <>
                  <button
                    onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                    className="flex items-center justify-between w-full px-3 py-2.5 text-white/80 hover:text-white"
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider">
                      Administración
                    </span>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 transition-transform',
                        adminMenuOpen && 'rotate-180'
                      )}
                    />
                  </button>
                  {adminMenuOpen && (
                    <div className="mt-1 space-y-1">
                      {adminNavItems.map((item) => (
                        <NavLinkItem key={item.href} item={item} />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                adminNavItems.map((item) => (
                  <NavLinkItem key={item.href} item={item} />
                ))
              )}
            </div>
          )}

          {/* Super Admin Section */}
          {isSuperAdmin && (
            <div className="pt-4 mt-4 border-t border-white/10">
              {sidebarOpen ? (
                <>
                  <button
                    onClick={() => setSuperAdminMenuOpen(!superAdminMenuOpen)}
                    className="flex items-center justify-between w-full px-3 py-2.5 text-white/80 hover:text-white"
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider text-amber-400">
                      Super Admin
                    </span>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 transition-transform text-amber-400',
                        superAdminMenuOpen && 'rotate-180'
                      )}
                    />
                  </button>
                  {superAdminMenuOpen && (
                    <div className="mt-1 space-y-1">
                      {superAdminNavItems.map((item) => (
                        <NavLinkItem key={item.href} item={item} />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                superAdminNavItems.map((item) => (
                  <NavLinkItem key={item.href} item={item} />
                ))
              )}
            </div>
          )}
        </nav>

        {/* User Section */}
        <div className="p-3 border-t border-white/10 space-y-1">
          {/* User Profile Link */}
          <NavLink
            to={APP_ROUTES.PROFILE}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 w-full',
                'text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-hover)]',
                isActive && 'bg-[var(--sidebar-active)]',
                !sidebarOpen && 'justify-center'
              )
            }
          >
            <div className="w-8 h-8 rounded-full bg-[var(--color-accent-500)] flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
              {user?.email?.[0]?.toUpperCase()}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {nombreParaMostrar(user)}
                </p>
                <p className="text-xs text-white/60 truncate">{user?.role}</p>
              </div>
            )}
          </NavLink>

          {/* El manual, a un clic. Se publica junto al producto, asi que el
              enlace nunca apunta a una version vieja del documento. */}
          <a
            href="/manual/"
            target="_blank"
            rel="noopener noreferrer"
            title="Manual de uso"
            className={cn(
              'flex items-center rounded-md px-4 py-2 text-sm font-medium text-white/80',
              'transition-colors hover:bg-[var(--sidebar-hover)] hover:text-white',
              !sidebarOpen && 'justify-center px-0'
            )}
          >
            <BookOpen className="h-5 w-5" />
            {sidebarOpen && <span className="ml-3">Manual de uso</span>}
          </a>

          {/* Logout Button */}
          <Button
            variant="ghost"
            className={cn(
              'w-full justify-start text-white/80 hover:text-white hover:bg-[var(--sidebar-hover)]',
              !sidebarOpen && 'justify-center px-0'
            )}
            onClick={() => logout()}
            disabled={isLoading}
          >
            <LogOut className="h-5 w-5" />
            {sidebarOpen && <span className="ml-3">Cerrar Sesión</span>}
          </Button>
        </div>
      </aside>

      {/* Velo del cajon: cerrar tocando fuera es el gesto esperado en movil,
          y ademas impide interactuar con el contenido de debajo por error. */}
      {!enPantallaAncha && sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
          aria-label="Cerrar menú"
        />
      )}

      {/* Main Content */}
      <div
        className={cn(
          'flex-1 flex flex-col transition-all duration-300 min-w-0',
          // Solo se aparta cuando el menu ocupa sitio de verdad. Con el
          // cajon superpuesto el contenido usa el ancho completo.
          enPantallaAncha ? (sidebarOpen ? 'ml-64' : 'ml-16') : 'ml-0'
        )}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-40 flex items-center gap-2 md:gap-4 h-16 px-4 md:px-6 bg-[var(--background)] border-b border-[var(--border)]">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-[var(--secondary)] transition-colors"
          >
            {sidebarOpen ? (
              <X className="h-5 w-5 text-[var(--foreground-muted)]" />
            ) : (
              <Menu className="h-5 w-5 text-[var(--foreground-muted)]" />
            )}
          </button>

          {/* Breadcrumb or Page Title */}
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-[var(--foreground)]">
              {getPageTitle(location.pathname)}
            </h2>
          </div>

          {/* El tema va en la cabecera y no escondido en ajustes: el
              radiologo lo cambia al entrar a la sala de lectura, no una vez
              en la vida. */}
          <InterruptorDeTema />

          {/* Notifications - Only render when authenticated to prevent 401 errors */}
          {user && !isLoading && <NotificationDropdown />}
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6">
          <LimiteDeError>
            <Outlet />
          </LimiteDeError>
        </main>
      </div>
    </div>
  )
}

// Helper to get page title from path
function getPageTitle(pathname: string): string {
  const titles: Record<string, string> = {
    [APP_ROUTES.DASHBOARD]: 'Dashboard',
    [APP_ROUTES.PROFILE]: 'Mi Perfil',
    [APP_ROUTES.PATIENTS]: 'Pacientes',
    [APP_ROUTES.STUDIES]: 'Estudios',
    [APP_ROUTES.WORKLIST]: 'Mi Trabajo',
    [APP_ROUTES.REPORTS]: 'Informes',
    [APP_ROUTES.TRANSCRIPTION.QUEUE]: 'Cola de Transcripción',
    [APP_ROUTES.ADMIN.USERS]: 'Usuarios',
    [APP_ROUTES.ADMIN.STUDY_TYPES]: 'Tipos de Estudio',
    [APP_ROUTES.ADMIN.TEMPLATES]: 'Plantillas de Informe',
    [APP_ROUTES.ADMIN.HOT_FOLDERS]: 'Carga Automática',
    [APP_ROUTES.ADMIN.AUDIT]: 'Auditoría',
    [APP_ROUTES.ADMIN.SEDES]: 'Sedes y equipos',
    [APP_ROUTES.ADMIN.SETTINGS]: 'Configuración',
    [APP_ROUTES.ADMIN.PRIORITIES]: 'Prioridades',
    [APP_ROUTES.SUPER_ADMIN.DASHBOARD]: 'Panel Super Admin',
    [APP_ROUTES.SUPER_ADMIN.INSTITUTIONS]: 'Instituciones',
    [APP_ROUTES.ADMIN_SCHEDULES]: 'Agendas',
    [APP_ROUTES.APPOINTMENTS]: 'Citas',
    [APP_ROUTES.APPOINTMENT_NEW]: 'Nueva Cita',
  }

  // Handle dynamic routes
  if (pathname.startsWith('/appointments/') && pathname !== '/appointments/new') {
    return 'Detalle de Cita'
  }

  if (pathname.startsWith('/transcription/')) {
    return 'Transcripción'
  }

  if (pathname.startsWith('/admin/hot-folders/') && pathname.includes('/files')) {
    return 'Archivos Hot Folder'
  }

  if (pathname.startsWith('/super-admin/')) {
    return 'Super Admin'
  }

  return titles[pathname] || PRODUCTO.nombre
}
